import React, { useState, useEffect, useCallback } from 'react';
import { dealApprovalsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  ShieldCheck, Plus, Trash2, CheckCircle, XCircle, Clock,
  AlertTriangle, Loader2, DollarSign, Percent, ChevronDown
} from 'lucide-react';

export default function DealApprovals() {
  const [activeTab, setActiveTab] = useState('pending');

  return (
    <div className="space-y-6" data-testid="deal-approvals-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Deal Approval Workflows</h1>
        <p className="text-sm text-muted-foreground mt-1">Multi-level approval for discounts and large deals</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="approval-tabs">
          <TabsTrigger value="pending" data-testid="tab-pending">Pending Approvals</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules">Approval Rules</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4"><PendingApprovalsTab /></TabsContent>
        <TabsContent value="rules" className="mt-4"><ApprovalRulesTab /></TabsContent>
        <TabsContent value="history" className="mt-4"><ApprovalHistoryTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ─── Pending Approvals ─── */
function PendingApprovalsTab() {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await dealApprovalsAPI.getPending();
      setApprovals(res.data.approvals || []);
    } catch (e) { toast.error('Failed to load approvals'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (id, action) => {
    try {
      await dealApprovalsAPI.takeAction(id, { action, comment: '' });
      toast.success(`Deal ${action}d successfully`);
      load();
    } catch (e) { toast.error(`Failed to ${action} deal`); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-3" data-testid="pending-approvals">
      {approvals.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
          <ShieldCheck className="h-10 w-10 mx-auto mb-3 text-emerald-500/50" />
          No pending approvals
        </CardContent></Card>
      ) : approvals.map(a => (
        <Card key={a.id} data-testid={`approval-${a.id}`}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-sm">{a.deal_name || 'Unnamed Deal'}</h3>
                  <Badge variant="outline" className="text-amber-500 border-amber-500/30">
                    <Clock className="h-3 w-3 mr-1" />Pending
                  </Badge>
                </div>
                <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                  <span>Value: ${(a.deal_value || 0).toLocaleString()}</span>
                  {a.max_discount_pct > 0 && <span>Max Discount: {a.max_discount_pct}%</span>}
                  <span>By: {a.submitted_by_name}</span>
                </div>
                {a.triggered_rules?.length > 0 && (
                  <div className="flex gap-1 mt-1.5">
                    {a.triggered_rules.map((r, i) => (
                      <Badge key={i} variant="secondary" className="text-[10px]">
                        <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />{r.rule_name}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="text-red-500 border-red-500/30 hover:bg-red-500/10"
                  onClick={() => handleAction(a.id, 'reject')} data-testid={`reject-${a.id}`}>
                  <XCircle className="h-3.5 w-3.5 mr-1" />Reject
                </Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => handleAction(a.id, 'approve')} data-testid={`approve-${a.id}`}>
                  <CheckCircle className="h-3.5 w-3.5 mr-1" />Approve
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* ─── Approval Rules ─── */
function ApprovalRulesTab() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', condition_type: 'deal_value', condition_operator: 'gt', condition_value: '', approver_role: 'manager' });

  const load = useCallback(async () => {
    try {
      const res = await dealApprovalsAPI.getRules();
      setRules(res.data.rules || []);
    } catch (e) { toast.error('Failed to load rules'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name || !form.condition_value) return toast.error('Fill in all fields');
    try {
      await dealApprovalsAPI.createRule({ ...form, condition_value: parseFloat(form.condition_value) });
      toast.success('Rule created');
      setShowForm(false);
      setForm({ name: '', condition_type: 'deal_value', condition_operator: 'gt', condition_value: '', approver_role: 'manager' });
      load();
    } catch (e) { toast.error('Failed to create rule'); }
  };

  const handleDelete = async (id) => {
    try {
      await dealApprovalsAPI.deleteRule(id);
      toast.success('Rule deleted');
      load();
    } catch (e) { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-3" data-testid="approval-rules">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setShowForm(!showForm)} data-testid="add-rule-btn">
          <Plus className="h-3.5 w-3.5 mr-1" />Add Rule
        </Button>
      </div>

      {showForm && (
        <Card data-testid="rule-form">
          <CardContent className="pt-4 pb-4 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Input placeholder="Rule name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="rule-name-input" />
              <select className="border rounded-md px-3 py-2 text-sm bg-background" value={form.condition_type}
                onChange={e => setForm({ ...form, condition_type: e.target.value })} data-testid="rule-condition-type">
                <option value="deal_value">Deal Value</option>
                <option value="discount_pct">Discount %</option>
              </select>
              <select className="border rounded-md px-3 py-2 text-sm bg-background" value={form.condition_operator}
                onChange={e => setForm({ ...form, condition_operator: e.target.value })} data-testid="rule-operator">
                <option value="gt">Greater than</option>
                <option value="gte">Greater or equal</option>
                <option value="lt">Less than</option>
              </select>
              <Input type="number" placeholder="Value" value={form.condition_value} onChange={e => setForm({ ...form, condition_value: e.target.value })} data-testid="rule-value-input" />
              <select className="border rounded-md px-3 py-2 text-sm bg-background" value={form.approver_role}
                onChange={e => setForm({ ...form, approver_role: e.target.value })} data-testid="rule-approver-role">
                <option value="manager">Manager</option>
                <option value="director">Director</option>
                <option value="vp">VP</option>
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button size="sm" onClick={handleCreate} data-testid="save-rule-btn">Save Rule</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {rules.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
          No approval rules configured. Add a rule to enable deal approvals.
        </CardContent></Card>
      ) : rules.map(r => (
        <Card key={r.id} data-testid={`rule-${r.id}`}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-sm">{r.name}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  When {r.condition_type === 'deal_value' ? 'deal value' : 'discount %'} is {r.condition_operator === 'gt' ? '>' : r.condition_operator === 'gte' ? '>=' : '<'}{' '}
                  {r.condition_type === 'deal_value' ? `$${r.condition_value.toLocaleString()}` : `${r.condition_value}%`}
                  {' '}&rarr; Requires {r.approver_role} approval
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={r.is_active ? 'default' : 'secondary'}>{r.is_active ? 'Active' : 'Inactive'}</Badge>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDelete(r.id)} data-testid={`delete-rule-${r.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* ─── Approval History ─── */
function ApprovalHistoryTab() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await dealApprovalsAPI.getHistory({});
        setHistory(res.data.approvals || []);
      } catch (e) { toast.error('Failed to load history'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const statusIcon = { approved: CheckCircle, rejected: XCircle, pending: Clock };
  const statusColor = { approved: 'text-emerald-500', rejected: 'text-red-500', pending: 'text-amber-500' };

  return (
    <div className="space-y-2" data-testid="approval-history">
      {history.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">No approval history</CardContent></Card>
      ) : history.map(h => {
        const Icon = statusIcon[h.status] || Clock;
        const color = statusColor[h.status] || 'text-muted-foreground';
        return (
          <Card key={h.id} data-testid={`history-${h.id}`}>
            <CardContent className="pt-3 pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Icon className={`h-5 w-5 ${color}`} />
                  <div>
                    <p className="text-sm font-medium">{h.deal_name || 'Deal'}</p>
                    <p className="text-xs text-muted-foreground">${(h.deal_value || 0).toLocaleString()} &middot; {h.submitted_by_name}</p>
                  </div>
                </div>
                <div className="text-right">
                  <Badge variant={h.status === 'approved' ? 'default' : h.status === 'rejected' ? 'destructive' : 'secondary'}>
                    {h.status?.toUpperCase()}
                  </Badge>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {h.updated_at ? new Date(h.updated_at).toLocaleDateString() : ''}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
